/**
 * External Accountant Portal Routes — JWT auth ONLY.
 *
 * Every route exported from this module is JWT-gated via
 * `AccountantAuthContext` (see `frontend/src/contexts/AccountantAuthContext.tsx`).
 * They serve the external witness-accountant portal, NOT in-house Clerk-authed
 * staff accountants. In-house staff use `/dashboard/accountant` (Clerk-gated),
 * which is declared in `App.tsx` separately.
 *
 * **Violating this invariant breaks the attestation auth boundary** — the
 * external accountant's signature is what makes the report admissible, so the
 * routes they touch must not silently accept Clerk-issued tokens. Any new
 * external-portal page belongs here; any new Clerk-gated page stays in
 * App.tsx.
 *
 * Attestation Phase 00.2 (docs/plans/2026-04-22-accountant-attestation).
 */
import React from 'react';
import { Navigate, Route, useParams } from 'react-router-dom';

import AccountantLogin from '../pages/AccountantLogin';
import AccountantRegister from '../pages/AccountantRegister';
import AccountantReportView from '../pages/AccountantReportView';
import ExternalAccountantWorkDesk from '../pages/workdesk/external-accountant/WorkDesk';
// import AccountantSharedReports from '../pages/AccountantSharedReports'; // Phase 3.1 — consolidated into WorkDesk
// import AccountantHome from '../pages/AccountantHome'; // Phase 3.1 — consolidated into WorkDesk
import AccountantComments from '../pages/AccountantComments';
import AccountantProfilePage from '../pages/AccountantProfilePage';
import AccountantAnomalyReview from '../pages/AccountantAnomalyReview';
import AccountantReconciliation from '../pages/AccountantReconciliation';
import AccountantExportCenter from '../pages/AccountantExportCenter';
import AccountantEngineReviewLanding from '../pages/AccountantEngineReviewLanding';
import AccountantEngineReviewDetail from '../pages/AccountantEngineReviewDetail';
import NarratorPage from '../pages/NarratorPage';
import AcceptInvite from '../pages/AcceptInvite';
import { CaseWorkspaceShell } from '../components/workspace';

/**
 * Redirects the legacy singular `/accountant/report/:reportId` to the
 * canonical plural `/accountant/reports/:reportId`. Phase 00.1.
 */
function RedirectToPluralReport() {
  // require() does not exist in the vite/ESM browser bundle — the old inline
  // require crashed every visit to the legacy singular /accountant/report/:id
  // URL into the global error boundary (found by the 2026-07-28 page sweep).
  const { reportId } = useParams<{ reportId: string }>();
  return <Navigate to={`/accountant/reports/${reportId}`} replace />;
}

/**
 * Every route rendered by this element tree. Exported as a fragment so
 * App.tsx can drop it into its `<Routes>` as a single JSX expression.
 *
 * Keep this list in sync with `EXTERNAL_ACCOUNTANT_ROUTE_PATHS` below;
 * `externalAccountantRoutes.test.ts` asserts the two match.
 */
export const externalAccountantRoutes = (
  <>
    {/* Auth */}
    <Route path="/accountant/login" element={<AccountantLogin />} />
    <Route path="/accountant/register" element={<AccountantRegister />} />

    {/* Legacy redirects — the external portal intentionally has no
        "/accountant/dashboard" of its own; staff accountants use
        `/dashboard/accountant` (Clerk-gated), external accountants land
        on `/accountant/reports`. These redirects preserve old bookmarks. */}
    <Route
      path="/accountant/dashboard"
      element={<Navigate to="/dashboard/accountant" replace />}
    />
    <Route
      path="/accountant"
      element={<Navigate to="/dashboard/accountant?tab=queue" replace />}
    />

    {/* Phase 3.1 redirects — external WorkDesk consolidation */}
    <Route path="/accountant/home" element={<Navigate to="/workdesk/accountant-external" replace />} />
    <Route path="/accountant/reports" element={<Navigate to="/workdesk/accountant-external" replace />} />
    {/* The external WorkDesk lives in THIS JWT tree (self-guards via useAccountantAuth),
        NOT behind App.tsx's Clerk <ProtectedRoute> — external CPAs have no Clerk session. */}
    <Route path="/workdesk/accountant-external" element={<ExternalAccountantWorkDesk />} />
    <Route path="/accountant/reports/:reportId" element={<AccountantReportView />} />
    <Route path="/accountant/report/:reportId" element={<RedirectToPluralReport />} />

    {/* Case surfaces */}
    <Route path="/accountant/comments" element={<AccountantComments />} />
    <Route path="/accountant/profile" element={<AccountantProfilePage />} />
    <Route path="/accountant/anomalies/:reportId" element={<AccountantAnomalyReview />} />
    <Route path="/accountant/reconciliation/:reportId" element={<AccountantReconciliation />} />
    <Route path="/accountant/review/:reportId" element={<AccountantEngineReviewLanding mode="external" />} />
    <Route path="/accountant/review/:reportId/:engineId" element={<AccountantEngineReviewDetail mode="external" />} />
    <Route path="/accountant/case/:reportId/export" element={<AccountantExportCenter />} />
    <Route path="/accountant/narrator/:reportId" element={<NarratorPage />} />

    {/* Phase 4 — Accountant Case Workspace routes. Rendered unconditionally so
        the route count stays in sync with EXTERNAL_ACCOUNTANT_ROUTE_PATHS;
        CaseWorkspaceShell self-guards on the CASE_WORKSPACE flag (renders null
        when off), so this does not expose the feature before it ships. */}
    <Route path="/accountant/reports/:reportId/:stage" element={<CaseWorkspaceShell role="accountant" />} />
    <Route path="/accountant/reports/:reportId/:stage/:panel" element={<CaseWorkspaceShell role="accountant" />} />

    {/* Invitation onboarding */}
    <Route path="/accountant/accept-invite/:token" element={<AcceptInvite />} />
  </>
);

/**
 * Canonical list of the route paths this module declares. Consumed by
 * `externalAccountantRoutes.test.ts` as the source of truth for the
 * JWT-auth boundary. If you add a route above, add its path here too.
 */
export const EXTERNAL_ACCOUNTANT_ROUTE_PATHS: readonly string[] = [
  '/accountant/login',
  '/accountant/register',
  '/accountant/dashboard',
  '/accountant',
  '/accountant/home',
  '/accountant/reports',
  '/workdesk/accountant-external',
  '/accountant/reports/:reportId',
  '/accountant/reports/:reportId/:stage',
  '/accountant/reports/:reportId/:stage/:panel',
  '/accountant/report/:reportId',
  '/accountant/comments',
  '/accountant/profile',
  '/accountant/anomalies/:reportId',
  '/accountant/reconciliation/:reportId',
  '/accountant/review/:reportId',
  '/accountant/review/:reportId/:engineId',
  '/accountant/case/:reportId/export',
  '/accountant/narrator/:reportId',
  '/accountant/accept-invite/:token',
];

export default externalAccountantRoutes;
